import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, type OnDestroy } from '@angular/core';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, switchMap, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { Group } from '../../../shared/models/api/group.model';
import type { User } from '../../../shared/models/api/user.model';
import { GroupService } from '../../../shared/services/api/group.service';

@Component({
  selector: 'opa-group-members-remove',
  imports: [CommonModule, PorscheDesignSystemModule],
  templateUrl: './group-members-remove.component.html',
  styleUrl: './group-members-remove.component.scss',
})
export class GroupMembersRemoveComponent implements OnDestroy {
  private readonly groupService = inject(GroupService);
  private readonly toastManager = inject(ToastManager);

  @Input({ required: true }) open = false;
  @Input({ required: true }) group: Group | undefined = undefined;
  @Input({ required: true }) user: User | undefined = undefined;
  @Output() dismiss = new EventEmitter<Group | undefined>();

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(group?: Group): void {
    this.dismiss.emit(group);
  }

  submit(): void {
    const group = this.group;
    const user = this.user;
    if (!group || !user) return;

    this.submitting.next(true);
    this.subSink.sink = this.groupService
      .removeGroupMember(group.id, user.id)
      .pipe(
        switchMap(() => this.groupService.getGroup(group.id)),
        tap({
          next: (updatedGroup) => {
            this.toastManager.addMessage({
              text: `Member ${user.firstName} ${user.lastName} removed from group ${updatedGroup.name}`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(updatedGroup);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }
}
